import { extractLanguage } from './lang/lang';
import { store } from './setup/store';
import { getLocale } from './lang/langSlice';
import { accessToken } from './tokenHandling/accessToken';
import { accessTokenStored, idTokenStored } from './tokenHandling/tokenSlice';
import { userProfileObtained, userSessionExpired, userSessionRenewed } from './login/loginSlice';
import { RioUserManager, type OAuthConfig, type SessionRenewedResult } from '../../src-login-lib';
import { EVENT_USER_LANGUAGE_CHANGED, EVENT_USER_PROFILE_CHANGED } from '@rio-cloud/rio-user-menu-component';
import { config } from '../config';
import { configureFetchDisplayMessages } from './lang/services';
import { reportErrorToSentry } from './setup/sentry';

const getFederationFt = () => {
    // fragment should be of a shape like
    // .../#/routePath?idp=foo
    // .../#?idp=foo
    const hash = window.location.hash.split('?')[1];
    const params = new URLSearchParams(hash);
    // valid idp values are eg cloud-vwgroup-com-qa or azure-my-rio-de
    return params.get('idp');
};

export const main = async (renderApp: () => void) => {
    const fetchDisplayMessages = configureFetchDisplayMessages(store);

    // We want the `<html lang>` attribute to be synced with the
    // language currently displayed
    store.subscribe(() => {
        const lang = extractLanguage(getLocale(store.getState())) as string;

        const html = document.querySelector('html');
        if (html && lang && html.getAttribute('lang') !== lang) {
            html.setAttribute('lang', lang);
        }
    });

    const oauthConfig: OAuthConfig = {
        onSessionExpired: () => {
            accessToken.discardAccessToken();
            store.dispatch(userSessionExpired());
        },
        onSessionRenewed: (result: SessionRenewedResult) => {
            // trace('index.onTokenRenewed', result);

            accessToken.saveAccessToken(result.accessToken);
            store.dispatch(accessTokenStored(result.accessToken));
            store.dispatch(idTokenStored(result.idToken));
            store.dispatch(userProfileObtained(result.profile));

            store.dispatch(userSessionRenewed());

            // You will need to get the user language yourself then
            // you may fetch the suitable messages. Depending
            // on when and from where you fetch the user settings you might
            // want to employ a loading spinner while the request is ongoing.
            fetchDisplayMessages(result.locale);
        },
    };

    const rioUserManager = new RioUserManager(oauthConfig, {
        oauthScope: config.login.oauthScope,
        clientId: `${config.login.clientId}`,
        redirectUri: `${config.login.redirectUri}`,
        authority: `${config.login.authority}`,
        mock: config.login.mockAuthorization,
    });

    const signinSilent = rioUserManager.signinSilent.bind(rioUserManager);
    document.addEventListener(EVENT_USER_LANGUAGE_CHANGED, () => signinSilent());
    document.addEventListener(EVENT_USER_PROFILE_CHANGED, () => signinSilent());

    const federationFt = getFederationFt();

    try {
        await rioUserManager.clearStaleState();
        await rioUserManager.attemptInitialSignin({
            preventRedirect: false,
            redirectUri: `${config.login.redirectUri}`,
            reportError: reportErrorToSentry,
            extraQueryParams: federationFt
                ? {
                      idp: federationFt,
                  }
                : undefined,
        });
        renderApp();
    } catch (error) {
        // trace('could not start application', error);
        reportErrorToSentry(error);
    }
};
